/**
 * Number validation utilities for form inputs
 * Simple validation logic that can be used directly with InputField
 */

/**
 * Validates if a string represents a valid number format
 * @param {string} value - The string to validate
 * @returns {boolean} - True if valid number format
 */
export const isValidNumberFormat = (value) => {
  if (!value || value === '') return true;

  // Check for scientific notation
  const hasScientificNotation = /[eE]/.test(value);
  if (hasScientificNotation) {
    // Allow valid scientific notation format with both positive and negative exponents
    const sciRegex = /^-?\d+(\.\d+)?(e[+-]?\d+)?$/i;
    return sciRegex.test(value) && !isNaN(Number(value));
  }

  // Allow decimal point as first character (e.g., ".5")
  const isValidDecimalStart = /^\.\d*$/.test(value);

  // Check for valid number pattern
  const isValidPattern = /^\d*(\.\d*)?$/.test(value) || isValidDecimalStart;

  return isValidPattern && !isNaN(Number(value));
};

/**
 * Validates if a value has invalid leading zeros
 * @param {string} value - The string to validate
 * @returns {boolean} - True if has invalid leading zeros
 */
export const hasInvalidLeadingZeros = (value) => {
  if (!value) return false;

  // Prevent leading zeros except for decimal numbers (e.g., "0.5" is allowed, "012" is not)
  const hasLeadingZero = /^0\d+$/.test(value); // "012", "03", etc.
  const isValidZeroDecimal = /^0\.\d*$/.test(value); // "0.5", "0.8", etc.

  return hasLeadingZero && !isValidZeroDecimal;
};

/**
 * Handles number input change validation
 * @param {string} value - The input value
 * @param {Function} setValue - Function to set the field value
 * @param {Function} triggerTooltip - Function to trigger tooltip message
 * @returns {string|number} - The processed value
 */
export const handleNumberInputChange = (value, setValue, triggerTooltip) => {
  if (!value || value === '') {
    setValue(null);
    return null;
  }

  // Check for invalid leading zeros
  if (hasInvalidLeadingZeros(value)) {
    triggerTooltip('Invalid number format');
    return value; // Return current value without updating
  }

  const hasScientificNotation = /[eE]/.test(value);
  const numericValue = Number(value);
  const isValidDecimalStart = /^\.\d*$/.test(value);
  const isValidZeroDecimal = /^0\.\d*$/.test(value);
  const endsWithDecimal = value.endsWith('.');

  if (
    !isNaN(numericValue) &&
    !hasScientificNotation &&
    (/^\d*(\.\d*)?$/.test(value) || isValidDecimalStart) &&
    !endsWithDecimal // Don't convert to number if it ends with decimal point
  ) {
    // Convert to number after validation passes
    const finalValue = value === '' ? null : Number(value);
    setValue(finalValue);
    return finalValue;
  } else if (isValidDecimalStart || isValidZeroDecimal) {
    // Allow partial input like ".5" or "0.5" but keep as string for now
    setValue(value);
    return value;
  } else if (value !== '') {
    // Allow incomplete input like "9." but keep as string for now
    setValue(value);
    return value;
  } else {
    setValue(null);
    return null;
  }
};

/**
 * Handles number key down validation
 * @param {KeyboardEvent} e - The keyboard event
 * @param {Object} options - Validation options
 * @param {number} options.stepValue - Step value for arrow keys
 * @param {number} options.minValue - Minimum value
 * @param {number} options.maxValue - Maximum value
 * @param {Function} options.setValue - Function to set the field value
 * @returns {boolean} - True if event should be prevented
 */
export const handleNumberKeyDown = (
  e,
  { stepValue = 1, minValue, maxValue, setValue }
) => {
  const key = e?.key;
  const input = e.currentTarget;
  const { value, selectionStart } = input;

  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

  const step = stepValue ?? 1;
  const min = minValue ?? Number.MIN_SAFE_INTEGER;
  const max = maxValue ?? Number.MAX_SAFE_INTEGER;

  // Handle arrow keys for increment/decrement
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    e.preventDefault();
    let current = parseFloat(value);
    if (isNaN(current)) current = 0;
    const newValue = key === 'ArrowUp' ? current + step : current - step;
    const clampedValue = Math?.min(Math.max(newValue, min), max);
    setValue(Number(clampedValue));
    return true;
  }

  // Allow copy/paste operations
  if (
    (e?.ctrlKey || e?.metaKey) &&
    ['a', 'c', 'v', 'x', 'z'].includes(key?.toLowerCase())
  ) {
    return false;
  }

  // Allow navigation keys
  if (allowedKeys.includes(key)) {
    return false;
  }

  // Prevent typing after minus sign at the beginning
  if (value?.startsWith('-') && selectionStart === 0) {
    e.preventDefault();
    return true;
  }

  // Allow digits
  if (/^\d$/.test(key)) {
    // Prevent leading zero (except for 0.5, 0.8, etc.)
    if (key === '0' && selectionStart === 0) {
      e.preventDefault();
      return true;
    }
    return false;
  }

  // Allow decimal point (dot) if:
  // - it's the first character (e.g., ".5"), OR
  // - there's no decimal point already, AND
  // - there's no 'e' or 'E' already
  if (
    key === '.' &&
    (selectionStart === 0 ||
      (!value?.includes('.') && !value?.toLowerCase()?.includes('e')))
  ) {
    return false;
  }

  // Allow 'e' or 'E' only if:
  // - there's no 'e' already, AND
  // - there's at least one digit before
  if (
    (key === 'e' || key === 'E') &&
    !value?.toLowerCase()?.includes('e') &&
    /\d/.test(value)
  ) {
    return false;
  }

  // Allow '-' only at the start or right after 'e'
  if (
    key === '-' &&
    (selectionStart === 0 || value?.[selectionStart - 1]?.toLowerCase() === 'e')
  ) {
    return false;
  }

  e.preventDefault();
  return true;
};

/**
 * Validates pasted content for number inputs
 * @param {ClipboardEvent} e - The paste event
 * @param {Function} triggerTooltip - Function to trigger tooltip message
 * @returns {boolean} - True if paste should be prevented
 */
export const handleNumberPaste = (e, triggerTooltip) => {
  const pasted = e?.clipboardData?.getData('text');
  // Updated regex to allow both positive and negative exponents
  const sciRegex = /^-?\d+(\.\d+)?(e[+-]?\d+)?$/i;

  if (!sciRegex.test(pasted)) {
    e.preventDefault();
    triggerTooltip('Enter numbers only');
    return true;
  }

  return false;
};

/**
 * Hook for number validation that can be used with InputField
 * @param {Object} options - Validation options
 * @returns {Object} - Validation functions and handlers
 */
export const useNumberValidation = (options = {}) => {
  const {
    stepValue = 1,
    minValue,
    maxValue,
    setValue,
    triggerTooltip,
  } = options;

  const handleInputChange = (value) => {
    return handleNumberInputChange(value, setValue, triggerTooltip);
  };

  const handleKeyDown = (e) => {
    return handleNumberKeyDown(e, {
      stepValue,
      minValue,
      maxValue,
      setValue,
    });
  };

  const handlePaste = (e) => {
    return handleNumberPaste(e, triggerTooltip);
  };

  return {
    handleInputChange,
    handleKeyDown,
    handlePaste,
    isValidNumberFormat,
    hasInvalidLeadingZeros,
  };
};

export default useNumberValidation;
